import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Cookies from 'js-cookie'
import _ from 'lodash'
import { Field, reduxForm } from 'redux-form'
import {
  Form,
  Button,
  Alert,
  InputFieldReduxFormWrapper,
  CheckboxFieldReduxFormWrapper,
  PasswordFieldReduxFormWrapper,
  PhoneVerification,
} from '@adc/ui-core'
import classNames from 'classnames/bind'
import configment from '@adc/configment'
import UserService from '../../../../shared/services/user'
import validator from '../../../../util/authFormValidators'
import cls from './styles.scss'

const cx = classNames.bind(cls)

const { CUSTOMER_SUPPORT_PHONE_NUMBER, formatPhoneNumber } =
  PhoneVerification.utils

export const FORM_NAME = 'auth_login'
const validate = validator()[FORM_NAME]
const COOKIE_EXPIRATION_DAYS = 365

class LoginForm extends Component {
  static propTypes = {
    elmId: PropTypes.string,
    cls: PropTypes.object,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    cta: PropTypes.string,
    onFail: PropTypes.func,
    onSuccess: PropTypes.func,
    loginEndPoint: PropTypes.string,
    resendEmailLink: PropTypes.string,
    forgotPasswordLink: PropTypes.string,
    rememberEmailCookieName: PropTypes.string,
    submitting: PropTypes.bool.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object,
    disableEmail: PropTypes.bool,
    passwordOnly: PropTypes.bool,
    additionalFields: PropTypes.node,
  }

  static defaultProps = {
    elmId: 'auth_toggle_login',
    cls: {},
    title: 'Log In',
    subTitle: '',
    cta: 'Log In',
    onFail: _.noop,
    onSuccess: _.noop,
    loginEndPoint: '/apps/api/login',
    resendEmailLink: '/user/resend-confirm-email',
    forgotPasswordLink: '/user/forgot-password',
    rememberEmailCookieName: 'ADC_LGN_EML',
    disableEmail: false,
    additionalFields: null,
  }

  state = {
    errorCode: null,
    lastSubmittedEmail: '',
  }

  submit = async values => {
    const { onFail, onSuccess, loginEndPoint, rememberEmailCookieName } =
      this.props
    const { email, password, rememberEmail } = values
    this.setState({ lastSubmittedEmail: email })
    try {
      const authResponse = await UserService.login(
        email,
        password,
        loginEndPoint
      )
      if (!authResponse.body.login) throw authResponse
      if (rememberEmail) {
        Cookies.set(rememberEmailCookieName, email, {
          expires: COOKIE_EXPIRATION_DAYS,
        })
      } else {
        Cookies.remove(rememberEmailCookieName)
      }
      const user = _.get(authResponse, 'body.user')
      // Fall back to the submitted email so downstream tracking can hash it.
      return onSuccess({ email, ...user })
    } catch (err) {
      const errorCode = _.get(err, 'body.error_code', 'unknownError')
      this.setState({ errorCode })
      const authError = { ...err.body, authType: 'login' }
      return onFail(authError)
    }
  }

  renderError = () => {
    const { resendEmailLink } = this.props
    const { errorCode, lastSubmittedEmail } = this.state

    if (!errorCode) return

    switch (errorCode) {
      case 'ERR_UNACTIVATED_ACCOUNT':
        return (
          <>
            <span>
              The account associated with email address {lastSubmittedEmail} is
              pending confirmation.
            </span>
            &nbsp;
            <a target='_blank' rel='noreferrer' href={resendEmailLink}>
              Resend Confirmation Email.
            </a>
          </>
        )
      case 'ERR_SUSPENDED_ACCOUNT':
        return (
          <span>
            Your account has been suspended. Please call{' '}
            <a href={`tel:${CUSTOMER_SUPPORT_PHONE_NUMBER}`}>
              {formatPhoneNumber(CUSTOMER_SUPPORT_PHONE_NUMBER)}
            </a>{' '}
            and our Customer Care Team will assist you.
          </span>
        )
      case 'ERR_LOGIN_BLOCKED':
        return (
          <span>
            You have reached the max number of attempts.{' '}
            <a href='/user/forgot-password'>Please reset your password.</a>
          </span>
        )
      case 'ERR_TEMPORAL_LOGIN_BLOCKED':
        return (
          <span>
            You have reached the max number of attempts. You have been locked
            out for{' '}
            {configment.get(
              'SECURITY_TEMPORAL_BLOCKED_LOCKOUT_DURATION_MINS',
              '30'
            )}{' '}
            minutes. Try again later.
          </span>
        )
      case 'ERR_INVALID_GRANT':
        return 'Email or password is incorrect.'
      default:
        return 'An unknown error occurred.'
    }
  }

  render() {
    const {
      elmId,
      cls: extcls,
      title,
      subTitle,
      cta,
      forgotPasswordLink,
      submitting,
      handleSubmit,
      initialValues,
      disableEmail,
      passwordOnly,
      additionalFields,
    } = this.props
    const errorMessage = this.renderError()

    return (
      <div className={cx('root', extcls.root)} data-elm-id={elmId}>
        {!passwordOnly && (
          <div className={cx('header', extcls.header)}>
            <h2 className={cx('title')}>{title}</h2>
            {subTitle && <p>{subTitle}</p>}
          </div>
        )}

        <Alert
          elmId={`${elmId}_error_message`}
          className={cls['form-error']}
          role='error'
          showIcon
          content={errorMessage}
          hidden={!errorMessage}
          dismissible={false}
        />

        <Form
          className={cx('u-pt-1')}
          onSubmit={handleSubmit(this.submit)}
          data-elm-id={`${elmId}_form`}
        >
          <div className={cx({ 'hidden-email': passwordOnly })}>
            <Field
              autoFocus
              type='email'
              name='email'
              placeholder='Email'
              elmId={`${elmId}_email_address`}
              aria-label='login email'
              component={InputFieldReduxFormWrapper}
              className={cx('form-field')}
              hideLabel
              disabled={initialValues?.email && disableEmail}
            />
          </div>

          <Field
            type='password'
            name='password'
            placeholder='Password'
            elmId={`${elmId}_password`}
            aria-label='login password'
            component={PasswordFieldReduxFormWrapper}
            className={cx('form-field')}
            hideLabel
          />

          <div className={cx('form-field', 'form-options-root')}>
            <div className={cx('form-option', 'left')}>
              <Field
                hideLabel
                type='checkbox'
                name='rememberEmail'
                elmId={`${elmId}_remember_email`}
                aria-label='remember email'
                checkboxLabel={
                  <label className={cx('form-label')}>Remember my email</label>
                }
                component={CheckboxFieldReduxFormWrapper}
              />
            </div>
            <div className={cx('form-option', 'right')}>
              <a
                data-elm-id={`${elmId}_forgot_password`}
                href={forgotPasswordLink}
                target='_blank'
                rel='noreferrer'
              >
                Forgot password?
              </a>
            </div>
          </div>

          {additionalFields && (
            <div
              className={cx('form-field', 'additional-fields')}
              style={{ textAlign: 'right' }}
            >
              {additionalFields}
            </div>
          )}

          <Button
            fluid
            type='submit'
            role='primary'
            elmId={`${elmId}_login_button`}
            disabled={submitting}
            className={cx('form-field', extcls.cta)}
            loading={submitting}
          >
            {submitting ? 'Logging In...' : cta}
          </Button>
        </Form>

        <p
          className={cx(
            'form-field',
            'form-label',
            'footnote',
            extcls.footnote
          )}
        >
          By logging in you agree that we may use the information you provided
          to contact you by email, telephone or postal mail in connection with
          our services, including for marketing purposes.&nbsp;
          <a
            data-elm-id={`${elmId}_privacy_statement`}
            target='_blank'
            rel='noreferrer'
            href='https://www.auction.com/lp/legal/privacy/'
          >
            Privacy Statement
          </a>
          .
        </p>
      </div>
    )
  }
}

export default reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
  touchOnBlur: false,
})(LoginForm)
